import { useEffect, useState } from "react";
import { ethers } from "ethers";

const BASE_CHAIN_ID = "0x2105";
const LOVE_ADDRESS = "0x16A7F8CfAD687A87183fCbd1dF7aF09dce05D357";
const LOVE_ABI = ["function transfer(address to, uint256 amount) returns (bool)"];
const PRESETS = ["1", "5", "10"];

type Props = {
  faceId: string;
  handle: string;
  recipient?: string;
  verified?: boolean;
};

// 小白极简:选个数 → 一个大按钮「送 LOVE」(指挥舱直付);钱包方式收在「其他方式」里。
export default function LoveTransferPanel({ faceId, handle, recipient, verified }: Props) {
  const [amount, setAmount] = useState("1");
  const [custom, setCustom] = useState(false);
  const [status, setStatus] = useState<"idle" | "connecting" | "confirming" | "pending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [txHash, setTxHash] = useState<string | null>(null);
  const [showWallet, setShowWallet] = useState(false);

  const canPay = Boolean(recipient && verified && ethers.isAddress(recipient));

  // 从指挥舱付款返回:#love_tx=<hash>
  useEffect(() => {
    const hit = window.location.hash.match(/^#love_tx=(0x[0-9a-fA-F]{64})$/);
    if (hit) {
      setTxHash(hit[1]);
      setStatus("success");
      setMessage("LOVE 已送达 🎉");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const badAmount = () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setStatus("error");
      setMessage("数量要大于 0");
      return true;
    }
    return false;
  };

  // 免钱包:跳 u.jic.io 指挥舱,自动弹出预填转账窗,付完跳回本页
  const payViaCabin = () => {
    if (!canPay || !recipient || badAmount()) return;
    const params = new URLSearchParams({
      pay_to: recipient,
      pay_amount: amount,
      pay_token: "LOVE",
      pay_note: `@${handle}`,
      pay_return: window.location.href.split("#")[0],
    });
    window.location.href = `https://u.jic.io/u?${params.toString()}`;
  };

  const sendViaWallet = async () => {
    if (!canPay || !recipient || badAmount()) return;
    const injected = window.ethereum;
    if (!injected) {
      setStatus("error");
      setMessage("没找到钱包 App,请用钱包浏览器打开,或用上面的按钮");
      return;
    }
    try {
      setTxHash(null);
      setStatus("connecting");
      setMessage("正在连接钱包…");
      await injected.request({ method: "eth_requestAccounts" });
      try {
        await injected.request({ method: "wallet_switchEthereumChain", params: [{ chainId: BASE_CHAIN_ID }] });
      } catch (switchError) {
        const code = (switchError as { code?: number }).code;
        if (code !== 4902) throw switchError;
        await injected.request({
          method: "wallet_addEthereumChain",
          params: [{
            chainId: BASE_CHAIN_ID,
            chainName: "Base",
            nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
            rpcUrls: ["https://mainnet.base.org"],
            blockExplorerUrls: ["https://basescan.org"],
          }],
        });
      }
      setStatus("confirming");
      setMessage("在钱包里点确认…");
      const provider = new ethers.BrowserProvider(injected as unknown as ethers.Eip1193Provider);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(LOVE_ADDRESS, LOVE_ABI, signer);
      const transaction = await contract.transfer(recipient, ethers.parseUnits(amount, 18));
      setTxHash(transaction.hash);
      setStatus("pending");
      setMessage("发出去了,等链上确认…");
      await transaction.wait();
      setStatus("success");
      setMessage("LOVE 已送达 🎉");
    } catch (error) {
      const raw = error instanceof Error ? error.message : "没发出去";
      const rejected = /rejected|denied|cancelled|canceled/i.test(raw);
      setStatus("error");
      setMessage(rejected ? "已取消,没有转账" : "没发出去,再试一次");
    }
  };

  const busy = ["connecting", "confirming", "pending"].includes(status);

  if (status === "success") {
    return (
      <section className="fs-pay">
        <div className="fs-pay-done">
          <span>💚</span>
          <strong>{amount} LOVE 已送给 {faceId}</strong>
          {txHash && <a href={`https://basescan.org/tx/${txHash}`} target="_blank" rel="noreferrer">看记录 ↗</a>}
        </div>
      </section>
    );
  }

  return (
    <section className="fs-pay" aria-label="送 LOVE">
      {!canPay ? (
        <p className="fs-msg">TA 还没绑定收款账户,暂时不能收 LOVE</p>
      ) : (
        <>
          <div className="fs-amounts">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={!custom && amount === preset ? "on" : ""}
                onClick={() => { setCustom(false); setAmount(preset); }}
              >
                {preset}
              </button>
            ))}
            {custom ? (
              <input
                autoFocus
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                aria-label="自定义数量"
              />
            ) : (
              <button type="button" onClick={() => { setCustom(true); setAmount(""); }}>其他</button>
            )}
          </div>
          <button type="button" className="fs-pay-btn" onClick={payViaCabin} disabled={busy}>
            💛 送 {amount || "?"} LOVE
          </button>
          {message && <p className={`fs-msg ${status === "error" ? "bad" : ""}`}>{message}</p>}
          {!showWallet ? (
            <button type="button" className="fs-link" onClick={() => setShowWallet(true)}>其他方式</button>
          ) : (
            <button type="button" className="fs-link" onClick={sendViaWallet} disabled={busy}>
              {busy ? "钱包处理中…" : "🦊 用自己的钱包发送"}
            </button>
          )}
        </>
      )}
    </section>
  );
}
